import React from "react";
import { Helmet } from "react-helmet";
import { Search } from "lucide-react";
import OpticPickerIcon from "../components/OpticPickerIcon";

const HomePage = ({
  categories,
  searchTerm,
  setSearchTerm,
  handleSearch,
  handleCategoryClick,
  setCurrentPage,
}) => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>OpticPicker - Compare Rifle Scope & Optic Prices</title>
        <meta name="description" content="Find the best prices on rifle scopes, red dot sights, binoculars, night vision, and more. Compare deals across top firearms optics retailers." />
        <meta property="og:title" content="OpticPicker - Compare Rifle Scope & Optic Prices" />
        <meta property="og:description" content="Find the best prices on rifle scopes, red dot sights, binoculars, night vision, and more." />
        <meta property="og:url" content="https://www.opticpicker.com/" />
        <link rel="canonical" href="https://www.opticpicker.com/" />
      </Helmet>
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-900/40 to-purple-900/40 dark:from-blue-100/40 dark:to-purple-100/40 backdrop-blur-sm py-5">
        <div className="max-w-7xl mx-auto px-6 text-center">
          {/* Logo above title */}
          <div className="flex justify-center mb-0">
            <OpticPickerIcon size={300} />
          </div>

          <h1 className="text-6xl font-bold text-white dark:text-gray-900 mb-6 leading-tight">
            Find Your Optic
          </h1>
          <p className="text-xl text-gray-300 dark:text-gray-700 mb-8 max-w-2xl mx-auto">
            Compare prices, specs, and reviews across top retailers to make the
            best choice for your firearm.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-600 w-6 h-6" />
              <input
                type="text"
                placeholder="Search for optics..."
                className="w-full pl-14 pr-4 py-4 bg-white/10 dark:bg-gray-900/10 backdrop-blur-md border-2 border-white/20 dark:border-gray-900/20 rounded-2xl text-white dark:text-gray-900 placeholder-gray-400 dark:placeholder-gray-600 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
              <button
                onClick={handleSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-xl transition"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="text-4xl font-bold text-white dark:text-gray-900 mb-4 text-center">
          Browse by Category
        </h2>
        <p className="text-gray-400 dark:text-gray-600 text-center mb-12 max-w-2xl mx-auto">
          Explore our collection of premium optics organized by type
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryClick(category.name)}
              className="group relative overflow-hidden bg-gradient-to-br from-gray-800/60 to-gray-900/60 dark:from-gray-100/60 dark:to-gray-200/60 backdrop-blur-sm rounded-2xl p-8 border-2 border-gray-700/50 dark:border-gray-300/50 hover:border-blue-500/50 dark:hover:border-blue-600/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 to-purple-600/0 group-hover:from-blue-600/10 group-hover:to-purple-600/10 transition-all duration-300"></div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white dark:text-gray-900 mb-2">
                  {category.name}
                </h3>
                <p className="text-gray-400 dark:text-gray-600 mb-4">
                  {category.description}
                </p>
                <div className="flex items-center text-blue-400 dark:text-blue-600 font-semibold group-hover:gap-2 transition-all">
                  <span>Explore</span>
                  <span className="transform group-hover:translate-x-1 transition-transform">
                    →
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-blue-900/40 to-purple-900/40 dark:from-blue-100/40 dark:to-purple-100/40 backdrop-blur-sm rounded-3xl p-12 text-center border border-white/10 dark:border-gray-900/10">
          <h2 className="text-4xl font-bold text-white dark:text-gray-900 mb-4">
            Ready to Find Your Perfect Optic?
          </h2>
          <p className="text-xl text-gray-300 dark:text-gray-700 mb-8 max-w-2xl mx-auto">
            Browse our full collection and compare prices from top retailers
          </p>
          <button
            onClick={() => setCurrentPage("browse")}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-500 dark:to-cyan-500 hover:from-blue-500 hover:to-cyan-500 text-white font-bold rounded-xl transition transform hover:scale-105 shadow-lg shadow-blue-600/30 text-lg"
          >
            Browse All Optics
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
